import { AppRouterInstance } from 'next/dist/shared/lib/app-router-context.shared-runtime';
import statusConfig from '@/../status-config.json';
import { getActiveProjectsTabQuerySlugFromStaticTabKey } from '@/utils/activeProjectsTabQuerySlug';

interface VendorRedirectionParams {
  vendorStatus: string;
  vendorId: string | number;
  ideaId?: string | number;
  productId?: string | number;
  router: AppRouterInstance;
  context: 'VENDOR_ASSESSMENT' | 'BUSINESS_REVIEW' | 'TASTING_PANEL' | 'PREPANEL' | 'ACTIVE_PROJECTS' | 'ARTWORK';
  activeProjectsStaticTabKey?: string | null;
}

export interface RedirectVendorToStatusHomeParams {
  vendorAssessmentStatus: string;
  vendorId: string | number;
  productId: string | number;
  /** Optional: preserve clicked assessment when redirecting off Active Projects. */
  vendorAssessmentId?: string | number | null;
  router: AppRouterInstance;
  /** Current AP main tab key — maps to `tab` query via slug map (never from status-config). */
  activeProjectsStaticTabKey?: string | null;
}

/** Normalize status to match keys in status-config.json */
export const normalizeStatusForConfig = (status: string): string => {
  if (!status || typeof status !== 'string') {
    return '';
  }
  return status.toUpperCase().replace(/_/g, ' ').trim();
};

export const getStatusConfigEntry = (status: string) => {
  const normalized = normalizeStatusForConfig(status);
  if (!normalized) {
    return undefined;
  }
  return statusConfig[normalized as keyof typeof statusConfig];
};

/** True when status-config routes the vendor to Active Projects detail (not another workflow module). */
export const shouldStayOnActiveProjectsDetail = (status: string): boolean => {
  const entry = getStatusConfigEntry(status);
  if (!entry) {
    return true;
  }
  const url = String(entry.redirect_url || '').trim();
  return url === '/active-projects' || url.startsWith('/active-projects/');
};

/**
 * When the target assessment's home is not Active Projects, redirect to status-config workflow page.
 */
export const redirectVendorToStatusHome = ({
  vendorAssessmentStatus,
  vendorId,
  productId,
  vendorAssessmentId,
  router,
  activeProjectsStaticTabKey,
}: RedirectVendorToStatusHomeParams): boolean => {
  if (!vendorAssessmentStatus || !vendorId || !productId || !router) {
    return false;
  }

  const entry = getStatusConfigEntry(vendorAssessmentStatus);
  if (!entry) {
    return false;
  }

  if (shouldStayOnActiveProjectsDetail(vendorAssessmentStatus)) {
    return false;
  }

  const redirectUrl = `${entry.redirect_url}/${productId}`;
  const redirectParams: Record<string, string> = {
    vendorId: String(vendorId),
  };
  if (vendorAssessmentId != null && String(vendorAssessmentId).trim() !== "") {
    redirectParams.vendorAssessmentId = String(vendorAssessmentId).trim();
  }
  const tabSlug = getActiveProjectsTabQuerySlugFromStaticTabKey(activeProjectsStaticTabKey);
  if (tabSlug) {
    redirectParams.tab = tabSlug;
  }
  const redirectQuery = new URLSearchParams(redirectParams).toString();
  const finalRedirectUrl = redirectQuery ? `${redirectUrl}?${redirectQuery}` : redirectUrl;

  router.push(finalRedirectUrl);
  return true;
};

/**
 * Centralized vendor redirection logic that can be used both on initial page load
 * and when manually switching vendor tabs
 */
export const checkAndHandleVendorRedirection = ({
  vendorStatus,
  vendorId,
  ideaId,
  productId,
  router,
  context,
  activeProjectsStaticTabKey,
}: VendorRedirectionParams): boolean => {
  if (!vendorStatus || !vendorId || !router) {
    console.log(`⚠️ ${context} - Missing required parameters for redirect check`, {
      vendorStatus,
      vendorId,
      hasRouter: !!router
    });
    return false;
  }

  if (context === 'ACTIVE_PROJECTS') {
    if (!productId) {
      console.log(`⚠️ ${context} - NO PRODUCT ID FOUND - Cannot redirect`);
      return false;
    }
    return redirectVendorToStatusHome({
      vendorAssessmentStatus: vendorStatus,
      vendorId,
      productId,
      router,
      activeProjectsStaticTabKey,
    });
  }

  if (!ideaId) {
    console.log(`⚠️ ${context} - NO IDEA ID FOUND - Cannot redirect`);
    return false;
  }

  const normalizedStatus = normalizeStatusForConfig(vendorStatus);

  let allowedRedirectStatuses: string[] = [];

  switch (context) {
    case 'VENDOR_ASSESSMENT':
      allowedRedirectStatuses = [
        'VENDOR INFORMATION REQUESTED',
        'PARTIALLY SUBMITTED',
        'UNDER PRODUCT DEVELOPER REVIEW',
        'UNDER INTERNAL REVIEW',
        'PREPARE FOR LAUNCH',
        'ARTWORK IN DESIGN',
        'READY FOR ORDERS',
        'SUPERSEDED'
      ];
      break;
    case 'BUSINESS_REVIEW':
    case 'TASTING_PANEL':
    case 'PREPANEL':
      allowedRedirectStatuses = [
        'VENDOR INFORMATION REQUESTED',
        'PARTIALLY SUBMITTED',
        'UNDER PRODUCT DEVELOPER REVIEW',
        'UNDER INTERNAL REVIEW',
        'ARTWORK IN DESIGN',
        'READY FOR ORDERS',
        'SUPERSEDED'
      ];
      break;
    case 'ARTWORK':
      allowedRedirectStatuses = [
        'IN PROGRESS',
        'VENDOR ASSESSMENT',
        'VENDOR APPROVED',
        'AWAITING BUSINESS REVIEW',
        'READY FOR TASTING PANEL',
        'TASTING PANEL APPROVED',
        'VENDOR INFORMATION REQUESTED',
        'PARTIALLY SUBMITTED',
        'UNDER PRODUCT DEVELOPER REVIEW',
        'UNDER INTERNAL REVIEW',
        'NEED MORE INFORMATION',
        'NEEDS REVISION',
      ];
      break;
    default:
      console.log(`⚠️ Unknown context: ${context}`);
      return false;
  }

  const isAllowedForRedirect = allowedRedirectStatuses.includes(normalizedStatus);
  const statusConfigEntry = getStatusConfigEntry(vendorStatus);

  if (isAllowedForRedirect && statusConfigEntry) {
    const redirectUrl = `${statusConfigEntry.redirect_url}/${ideaId}`;
    const redirectParams: Record<string, string> = {
      vendorId: String(vendorId),
    };
    const redirectQuery = new URLSearchParams(redirectParams).toString();
    const finalRedirectUrl = redirectQuery ? `${redirectUrl}?${redirectQuery}` : redirectUrl;

    router.push(finalRedirectUrl);
    return true;
  }

  console.log(`⚠️ ${context} - STATUS NOT ALLOWED FOR REDIRECT OR NOT FOUND IN CONFIG`, {
    vendorStatus: normalizedStatus,
    isAllowedForRedirect,
    foundConfig: !!statusConfigEntry,
    allowedStatuses: allowedRedirectStatuses,
    availableStatuses: Object.keys(statusConfig)
  });
  return false;
};

/**
 * Prefer vendorAssessmentStatus for Active Projects vendor/version switches.
 */
export const extractVendorAssessmentStatus = (vendor: unknown): string => {
  if (!vendor || typeof vendor !== 'object') {
    return '';
  }
  const v = vendor as Record<string, unknown>;
  const vendorDetails = v.vendorDetails as Record<string, unknown> | undefined;
  const vendorAssessment = v.vendorAssessment as Record<string, unknown> | undefined;
  const nestedAssessment = vendorDetails?.vendorAssessment as Record<string, unknown> | undefined;

  const direct =
    v.vendorAssessmentStatus ??
    nestedAssessment?.vendorAssessmentStatus ??
    vendorAssessment?.vendorAssessmentStatus;

  if (direct != null && String(direct).trim() !== '') {
    return String(direct);
  }

  return extractVendorStatus(vendor, 'ACTIVE_PROJECTS');
};

/**
 * Helper function to extract vendor status from different vendor object structures
 */
export const extractVendorStatus = (vendor: any, context: string): string => {
  if (!vendor) return '';

  return (
    vendor.vendorDetails?.vendorAssessment?.businessStatus ||
    vendor.vendorAssessment?.businessStatus ||
    vendor.vendorAssessment?.vendorAssessmentStatus ||
    vendor.vendorAssessmentStatus ||
    vendor.status ||
    vendor.businessStatus ||
    ''
  );
};

/**
 * Helper function to extract vendor ID from different vendor object structures
 */
export const extractVendorId = (vendor: any, _context: string): string => {
  if (!vendor) return '';

  return String(
    vendor.vendorDetails?.id ||
    vendor.id ||
    vendor.vendorId ||
    ''
  );
};
